import { useTranslation } from '@/i18n/useTranslation';

import { useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { DatabaseBackupPanel } from '@/components/admin/DatabaseBackupPanel';
import {
  useAdminUsers,
  useCreateAdminUser,
  useDeactivateAdminUser,
  useUpdateAdminUser,
} from '@/hooks/useAdmin';
import { TableSkeleton } from '@/components/common/LoadingSkeleton';
import { InlineError } from '@/components/common/InlineError';
import { EmptyState } from '@/components/common/EmptyState';
import { Pagination } from '@/components/common/Pagination';
import type { UserRole } from '@/types';

const PAGE_SIZE = 20;

export function AdminUsersPage() {
  const { locale, t } = useTranslation();
  const { role: currentRole } = useAuth();
  const [page, setPage] = useState(1);
  const users = useAdminUsers({ page, page_size: PAGE_SIZE });
  const updateUser = useUpdateAdminUser();
  const createUser = useCreateAdminUser();
  const deactivateUser = useDeactivateAdminUser();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [canInfer, setCanInfer] = useState(true);
  const [canMobile, setCanMobile] = useState(true);
  const [canBackoffice, setCanBackoffice] = useState(true);
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState('');

  return (
    <div className="stack" style={{ gap: 'var(--space-5)' }}>
      <header>
        <h1>{t('Super Admin · Users')}</h1>
        <p>
          {t(
            'Create shared accounts, choose mobile and back-office access, or deactivate accounts.',
          )}
        </p>
      </header>

      {currentRole === 'super_admin' && <DatabaseBackupPanel />}

      <section className="glass-panel stack">
        <h2>{t('Add user')}</h2>
        <form
          className="cluster"
          onSubmit={(event) => {
            event.preventDefault();
            createUser.mutate(
              {
                username,
                email: email.trim() || null,
                display_name: name,
                password,
                role,
                can_infer: canInfer,
                can_use_mobile: canMobile,
                can_use_backoffice: canBackoffice,
              },
              {
                onSuccess: () => {
                  setUsername('');
                  setEmail('');
                  setName('');
                  setPassword('');
                },
              },
            );
          }}
        >
          <input
            className="input"
            aria-label={t('New username')}
            placeholder={t('Username')}
            minLength={3}
            maxLength={64}
            pattern="[A-Za-z0-9][A-Za-z0-9._-]{2,63}"
            required
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
          <input
            className="input"
            aria-label={t('New user name')}
            placeholder={t('Name')}
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <input
            className="input"
            aria-label={t('New user email optional')}
            type="email"
            placeholder={t('Email (optional)')}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <input
            className="input"
            aria-label={t('Initial password')}
            type="password"
            minLength={12}
            placeholder={t('Initial password')}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <select
            className="select"
            aria-label={t('New user role')}
            value={role}
            onChange={(event) => setRole(event.target.value as 'admin' | 'user')}
          >
            <option value="user">{t('User')}</option>
            <option value="admin">{t('Admin')}</option>
          </select>
          <label>
            <input
              type="checkbox"
              checked={canMobile}
              disabled={canMobile && !canBackoffice}
              onChange={(event) => setCanMobile(event.target.checked)}
            />{' '}
            {t('Mobile')}
          </label>
          <label>
            <input
              type="checkbox"
              checked={canBackoffice}
              disabled={canBackoffice && !canMobile}
              onChange={(event) => setCanBackoffice(event.target.checked)}
            />{' '}
            {t('Back office')}
          </label>
          <label>
            <input
              type="checkbox"
              checked={canInfer}
              onChange={(event) => setCanInfer(event.target.checked)}
            />{' '}
            {t('Inference')}
          </label>
          <button className="btn btn--primary" type="submit" disabled={createUser.isPending}>
            {t('Add user')}
          </button>
        </form>
        {createUser.isError && <InlineError error={createUser.error} />}
      </section>

      <section className="glass-panel">
        {users.isLoading && <TableSkeleton rows={PAGE_SIZE} cols={11} />}
        {users.isError && <InlineError error={users.error} onRetry={() => users.refetch()} />}
        {users.data && users.data.items.length === 0 && <EmptyState title={t('No users found')} />}
        {users.data && users.data.items.length > 0 && (
          <div className="stack">
            <div className="table-wrap">
              <table className="data-table">
                <caption className="visually-hidden">{t('Users')}</caption>
                <thead>
                  <tr>
                    <th>{t('Name')}</th>
                    <th>{t('Username')}</th>
                    <th>{t('Email')}</th>
                    <th>{t('Role')}</th>
                    <th>{t('Active')}</th>
                    <th>{t('Mobile')}</th>
                    <th>{t('Back office')}</th>
                    <th>{t('Inference')}</th>
                    <th>{t('Remove')}</th>
                    <th>{t('Reset password')}</th>
                    <th>{t('Last login')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.data.items.map((user) => (
                    <tr key={user.id}>
                      <td>{user.display_name}</td>
                      <td className="font-mono">{user.username || '—'}</td>
                      <td className="font-mono">{user.email || '—'}</td>
                      <td>
                        {user.role === 'super_admin' ? (
                          t('Super admin')
                        ) : (
                          <select
                            className="select"
                            aria-label={t('Role for {name}', { name: user.display_name })}
                            value={user.role}
                            disabled={updateUser.isPending}
                            onChange={(e) =>
                              updateUser.mutate({
                                id: user.id,
                                patch: { role: e.target.value as UserRole },
                              })
                            }
                          >
                            <option value="user">{t('User')}</option>
                            <option value="admin">{t('Admin')}</option>
                          </select>
                        )}
                      </td>
                      <td>
                        <label className="cluster" style={{ fontSize: 'var(--font-size-sm)' }}>
                          <input
                            type="checkbox"
                            aria-label={t('Active for {name}', { name: user.display_name })}
                            checked={user.is_active}
                            disabled={updateUser.isPending || user.role === 'super_admin'}
                            onChange={(e) =>
                              updateUser.mutate({
                                id: user.id,
                                patch: { is_active: e.target.checked },
                              })
                            }
                          />
                          {user.is_active ? t('Active') : t('Inactive')}
                        </label>
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={t('Mobile access for {name}', { name: user.display_name })}
                          checked={user.can_use_mobile}
                          disabled={
                            updateUser.isPending ||
                            user.role === 'super_admin' ||
                            (user.can_use_mobile && !user.can_use_backoffice)
                          }
                          onChange={(event) =>
                            updateUser.mutate({
                              id: user.id,
                              patch: { can_use_mobile: event.target.checked },
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={t('Back-office access for {name}', {
                            name: user.display_name,
                          })}
                          checked={user.can_use_backoffice}
                          disabled={
                            updateUser.isPending ||
                            user.role === 'super_admin' ||
                            (user.can_use_backoffice && !user.can_use_mobile)
                          }
                          onChange={(event) =>
                            updateUser.mutate({
                              id: user.id,
                              patch: { can_use_backoffice: event.target.checked },
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={t('Inference for {name}', { name: user.display_name })}
                          checked={user.can_infer}
                          disabled={updateUser.isPending || user.role === 'super_admin'}
                          onChange={(event) =>
                            updateUser.mutate({
                              id: user.id,
                              patch: { can_infer: event.target.checked },
                            })
                          }
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={
                            deactivateUser.isPending ||
                            !user.is_active ||
                            user.role === 'super_admin'
                          }
                          onClick={() => deactivateUser.mutate(user.id)}
                        >
                          {t('Deactivate')}
                        </button>
                      </td>
                      <td>
                        {user.role !== 'super_admin' &&
                          (resetUserId === user.id ? (
                            <form
                              className="cluster"
                              onSubmit={(event) => {
                                event.preventDefault();
                                updateUser.mutate(
                                  { id: user.id, patch: { password: resetPassword } },
                                  {
                                    onSuccess: () => {
                                      setResetUserId(null);
                                      setResetPassword('');
                                    },
                                  },
                                );
                              }}
                            >
                              <input
                                type="password"
                                className="input"
                                aria-label={t('New password for {name}', {
                                  name: user.display_name,
                                })}
                                minLength={12}
                                required
                                value={resetPassword}
                                onChange={(event) => setResetPassword(event.target.value)}
                              />
                              <button
                                type="submit"
                                className="btn btn--ghost btn--sm"
                                disabled={updateUser.isPending}
                              >
                                {t('Save')}
                              </button>
                            </form>
                          ) : (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              onClick={() => setResetUserId(user.id)}
                            >
                              {t('Reset')}
                            </button>
                          ))}
                        {user.must_change_password && <small>{t('Change required')}</small>}
                      </td>
                      <td className="font-mono">
                        {user.last_login_at
                          ? new Date(user.last_login_at).toLocaleString(locale)
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={users.data.total}
              onPageChange={setPage}
            />
          </div>
        )}
        {updateUser.isError && <InlineError error={updateUser.error} />}
        {deactivateUser.isError && <InlineError error={deactivateUser.error} />}
      </section>
    </div>
  );
}
